import React, { useState, useEffect } from 'react';

import { Modal, Button, Form, Tag, Spin, message, Divider, Input, Cascader, Row, Col, InputNumber } from 'antd';
import { PlusOutlined, CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

import * as ToolRequest from '../../../tools/ToolRequest';

import SpinFull from '../../../components/SpinFull';

const formLayout = {
  labelCol: {
    sm: { span: 5 },
  },
  wrapperCol: {
    sm: { span: 16 },
  },
  labelWrap: true,
};

const formTailLayout = {
  wrapperCol: {
    sm: { offset: 5, span: 16 },
  },
};

function StationDashboardGraphCustomGraphEditModalDataEntry(props) {
  const {
    onChange,
    value,
    ios,
  } = props;

  const colors = [
    { value: '#CC4C4C', label: 'Red' },
    { value: '#4C9E4C', label: 'Green' },
    { value: '#4C6ECC', label: 'Blue' },
    { value: '#CCCC4C', label: 'Yellow' },
    { value: '#CC4CCC', label: 'Magenta' },
    { value: '#4CCCCE', label: 'Cyan' },
    { value: '#CC914C', label: 'Orange' },
    { value: '#9966CC', label: 'Purple' },
    { value: '#336633', label: 'Dark Green' },
    { value: '#CC6699', label: 'Pink' },
  ];

  return (
    <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
      {value.map((item, i) => {
        const io = item.ioId ? ios.find(io => io.children.some(child => child.value === item.ioId)).children.find(child => child.io.id === item.ioId).io : null;
        const ioType = io ? io.type : null;

        const hideMinMax = ['WATER_LEVEL_DIGITAL', 'WATER_LEVEL'].includes(ioType);

        const updateItem = (valueNew) => {
          const io = valueNew.ioId ? ios.find(io => io.children.some(child => child.value === valueNew.ioId)).children.find(child => child.io.id === valueNew.ioId).io : null;
          const ioType = io ? io.type : null;

          const itemNewUpdated = {
            ...value[i],
            ...valueNew,
          };

          if (['WATER_LEVEL_DIGITAL', 'WATER_LEVEL'].includes(ioType)) {
            itemNewUpdated.min = 0;
            itemNewUpdated.max = 2;
          }

          onChange([...value.map((itemNew, j) => i === j ? itemNewUpdated : itemNew)]);
        };

        return (
          <Row key={i} gutter={8}>
            <Col span={12}>
              <Cascader
                placeholder="Select IO"
                options={ios}
                value={item.ioId ? [ios.find(io => io.children.some(child => child.value === item.ioId)).value, item.ioId] : null}
                onChange={(valueNew, selectedOptions) => {
                  const dataNew = { ioId: valueNew[1] };
                  const io = selectedOptions[1];
                  if (!isNaN(io.io.settings.min)) {
                    dataNew.min = parseFloat(io.io.settings.min);
                  }
                  if (!isNaN(io.io.settings.max)) {
                    dataNew.max = parseFloat(io.io.settings.max);
                  }
                  updateItem(dataNew);
                }}
                allowClear={false}
              />
            </Col>
            <Col span={5}>
              <Cascader
                placeholder="Color"
                options={colors.map(option => ({
                  value: option.value,
                  label: (
                    <div style={{ display: 'flex', gap: 8, flexDirection: 'row', alignItems: 'center' }}>
                      <span style={{
                        width: 16,
                        height: 16,
                        backgroundColor: option.value,
                      }} />
                      <span>{option.label}</span>
                    </div>
                  ),
                }))}
                value={item.color}
                onChange={(valueNew) => updateItem({ color: valueNew[0] })}
                allowClear={false}
              />
            </Col>
            <Col
              span={3}
              style={{
                ...(hideMinMax ? {
                  display: 'none',
                } : {}),
              }}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Min Value"
                value={item.min}
                onChange={(valueNew) => updateItem({ min: valueNew })}
              />
            </Col>
            <Col
              span={3}
              style={{
                ...(hideMinMax ? {
                  display: 'none',
                } : {}),
              }}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Max Value"
                value={item.max}
                onChange={(valueNew) => updateItem({ max: valueNew })}
              />
            </Col>
            <Col span={1}>
              <Button
                type="link"
                shape="circle"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => {
                  onChange(value.filter((valueNew, j) => i !== j))
                }}
              />
            </Col>
          </Row>
        );
      })}
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={() => {
          const unselectedColor = colors.find(color => !value.some(item => item.color === color.value));
          const unselectedColorValue = unselectedColor ? unselectedColor.value : colors[0].value;
          onChange([
            ...value,
            {
              ioId: null,
              color: unselectedColorValue,
              min: null,
              max: null,
            },
          ]);
        }}
      >
        Add Data
      </Button>
    </div>
  );
};

export default function StationDashboardGraphCustomGraphEditModal(props) {
  const {
    stationId,
    zones,
    stationGraphId,
    visible,
    onOk,
    onCancel,
  } = props;

  const [loaded, setLoaded] = useState(!stationGraphId);
  const [loading, setLoading] = useState(false);

  const [stationGraph, setStationGraph] = useState(null);

  useEffect(() => {
    if (stationGraphId) {
      (async () => {
        // zone automation
        const stationGraphRes = await ToolRequest.request('GET', `/v1/station-graph/${stationGraphId}`);
        setStationGraph(stationGraphRes);

        setLoaded(true);
      })();
    } else {
      setLoaded(true);
    }
  }, []);

  const onFinish = async (values) => {
    setLoading(true);

    try {
      if (stationGraphId) {
        await ToolRequest.request('PUT', `/v1/station-graph/${stationGraphId}`, {
          name: values.name,
          data: values.data,
        });

        message.success('Update Custom Graph successfully');
      } else {
        await ToolRequest.request('POST', `/v1/station-graph`, {
          stationId,
          name: values.name,
          data: values.data,
        });

        message.success('Add Custom Graph successfully');
      }

      onOk();
    } catch (err) {
      setLoading(false);

      if (err?.response?.data) {
        switch (err.response.data) {
          default:
            return message.error('Unknown errors. Please retry');
        }
      }

      message.error('Unknown error. Please retry');
    }
  };

  const ios = zones.map(zone => ({
    value: zone.id,
    label: zone.name,
    children: zone.ios.filter(zoneIo => zoneIo.config.inputs).map(io => ({    // only sensors
      value: io.id,
      label: io.name,
      io,
    })),
  }));

  return (
    <Modal
      title={stationGraphId ? 'Edit Custom Graph' : 'Add Custom Graph'}
      visible={visible}
      onOk={onOk}
      onCancel={onCancel}
      width="80%"
      footer={null}
    >
      {loaded ? (
        <Spin spinning={loading}>
          <Form
            {...formLayout}
            initialValues={stationGraph}
            onFinish={onFinish}
          >
            <Form.Item
              {...formLayout}
              key="name"
              label="Name"
              name="name"
              rules={[
                {
                  required: true,
                  message: `Please enter Name`,
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              {...formLayout}
              key="data"
              label="Data"
              name="data"
              rules={[
                {
                  required: true,
                  message: `Please set up Data`,
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value.length) {
                      return Promise.reject(new Error('Please add at least one Data'));
                    } else if (value.some(item => !item.ioId || !item.color || isNaN(item.min) || isNaN(item.max))) {
                      return Promise.reject(new Error('Some Data fields are missing'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
              initialValue={[]}
            >
              <StationDashboardGraphCustomGraphEditModalDataEntry ios={ios} />
            </Form.Item>

            <Form.Item
              {...formTailLayout}
            >
              <Button type="primary" htmlType="submit" block>
                {stationGraphId ? 'Update' : 'Submit'}
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      ) : <SpinFull />}
    </Modal>
  );
};